// src/components/pages/surveys/SurveysAdminScreen.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  Chip, 
  IconButton, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  CircularProgress,
  Fab,
  Card,
  CardContent,
  Stack,
  Alert,
  Skeleton,
  InputAdornment,
  Badge,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
  Snackbar,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  BarChart as BarChartIcon, 
  Search as SearchIcon,
  PollOutlined as PollIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ExpandMore as ExpandMoreIcon,
  Today as TodayIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, doc, deleteDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  warning: '#F5A623',
  error: '#E74C3C',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D'
  },
  status: {
    active: '#27AE60',
    inactive: '#BDC3C7',
    draft: '#F39C12'
  }
};

const SurveysAdminScreen = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive'
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState(null);
  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [newSurveyTitle, setNewSurveyTitle] = useState('');
  const [newSurveyDescription, setNewSurveyDescription] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);

  // Memoized filtered surveys
  const filteredSurveys = useMemo(() => {
    let filtered = surveys;

    // Filter by search query
    if (searchQuery) {
      const queryLower = searchQuery.toLowerCase();
      filtered = filtered.filter(survey => 
        survey.título?.toLowerCase().includes(queryLower) || 
        survey.descripción?.toLowerCase().includes(queryLower)
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(survey => {
        if (filterStatus === 'active') return survey.active;
        if (filterStatus === 'inactive') return !survey.active;
        return true;
      });
    }

    return filtered;
  }, [surveys, searchQuery, filterStatus]);

  // Memoized statistics
  const stats = useMemo(() => {
    const total = surveys.length;
    const active = surveys.filter(s => s.active).length;
    const inactive = total - active;
    const totalResponses = surveys.reduce((acc, s) => acc + (s.respuestas || 0), 0);
    const avgResponsesPerSurvey = total > 0 ? Math.round(totalResponses / total) : 0;

    return {
      total,
      active,
      inactive,
      totalResponses,
      avgResponsesPerSurvey
    };
  }, [surveys]);

  // Load surveys
  const loadSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const surveysQuery = query(
        collection(db, 'surveys'),
        orderBy('createdAt', 'desc')
      );
      
      const surveysSnapshot = await getDocs(surveysQuery);
      const surveysArray = surveysSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSurveys(surveysArray);
      
    } catch (error) {
      console.error("Error al cargar encuestas:", error);
      showSnackbar('Error al cargar las encuestas', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load surveys on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await loadSurveys();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [loadSurveys]);

  // Utility functions
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Fecha desconocida';
    
    try {
      const date = timestamp instanceof Date ? 
        timestamp : 
        new Date(timestamp.seconds * 1000);
        
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }, []);

  // Handle survey deletion
  const handleDeleteSurvey = useCallback((survey) => {
    setSurveyToDelete(survey);
    setDeleteDialogVisible(true);
  }, []);

  const confirmDeleteSurvey = async () => {
    if (!surveyToDelete) return;
    
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'surveys', surveyToDelete.id));
      
      setSurveys(prev => prev.filter(s => s.id !== surveyToDelete.id));
      showSnackbar('Encuesta eliminada correctamente');
      
    } catch (error) {
      console.error("Error al eliminar encuesta:", error);
      showSnackbar('Error al eliminar la encuesta', 'error');
    } finally {
      setDeleting(false);
      setDeleteDialogVisible(false);
      setSurveyToDelete(null);
    }
  };

  // Handle survey creation
  const handleCreateSurvey = async () => {
    if (!newSurveyTitle.trim()) {
      showSnackbar('El título es obligatorio', 'warning');
      return;
    }
    
    setCreating(true);
    try {
      const newSurvey = {
        título: newSurveyTitle.trim(),
        descripción: newSurveyDescription.trim(),
        createdAt: serverTimestamp(),
        active: false, // Start as draft
        preguntas: [],
        respuestas: 0,
        createdBy: 'admin', // You might want to get this from auth context
        lastModified: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, 'surveys'), newSurvey);
      
      showSnackbar('Encuesta creada correctamente');
      
      // Navigate to edit screen
      navigate(`/admin/surveys/edit/${docRef.id}`);
      
      // Clean up
      setNewSurveyTitle('');
      setNewSurveyDescription('');
      setCreateDialogVisible(false);
      
    } catch (error) {
      console.error("Error al crear encuesta:", error);
      showSnackbar('Error al crear la encuesta', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Handle survey editing
  const handleEditSurvey = useCallback((survey) => {
    navigate(`/admin/surveys/edit/${survey.id}`);
  }, [navigate]);

  // Handle survey status toggle
  const handleToggleSurveyActive = async (survey) => {
    try {
      const newStatus = !survey.active;
      await updateDoc(doc(db, 'surveys', survey.id), {
        active: newStatus,
        lastModified: serverTimestamp()
      });
      
      setSurveys(prev => prev.map(s => 
        s.id === survey.id ? { ...s, active: newStatus } : s
      ));
      
      showSnackbar(`Encuesta ${newStatus ? 'activada' : 'desactivada'} correctamente`);
      
    } catch (error) {
      console.error("Error al actualizar encuesta:", error);
      showSnackbar('Error al actualizar la encuesta', 'error');
    }
  };

  // Handle view results
  const handleViewResults = useCallback((survey) => {
    navigate(`/admin/surveys/results/${survey.id}`);
  }, [navigate]);

  const handleViewPreview = useCallback((survey) => {
    navigate(`/admin/surveys/preview/${survey.id}`);
  }, [navigate]);

  // Stats Card Component
  const StatsCard = ({ title, value, subtitle, color, icon, trend }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}20`
      }
    }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}20`,
            color: color 
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              {value}
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.text.primary, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {trend && (
            <Box sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ color: COLORS.success }} />
              <Typography variant="caption" sx={{ color: COLORS.success }}>
                {trend}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const SurveySkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} md={6} lg={4} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="rectangular" width={80} height={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 4 }}>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="text" width={400} height={24} sx={{ mt: 1 }} />
          </Box>
          <SurveySkeleton />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 1 }}>
            Gestión de Encuestas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra las encuestas para los padres y miembros del AMPA
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total Encuestas"
              value={stats.total}
              color={COLORS.accent}
              icon={<PollIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Activas"
              value={stats.active}
              subtitle={`${stats.inactive} inactivas`}
              color={COLORS.status.active}
              icon={<CheckCircleIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Respuestas Totales"
              value={stats.totalResponses}
              color={COLORS.secondary}
              icon={<PeopleIcon />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Promedio/Encuesta"
              value={stats.avgResponsesPerSurvey}
              subtitle="respuestas"
              color={COLORS.primary}
              icon={<TrendingUpIcon />}
            />
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: COLORS.surface,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar encuestas por título o descripción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: COLORS.background,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.accent,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.accent,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: COLORS.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                SelectProps={{ native: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              >
                <option value="all">Todas</option>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogVisible(true)}
                fullWidth
                sx={{
                  backgroundColor: COLORS.accent,
                  '&:hover': { backgroundColor: COLORS.secondary },
                  borderRadius: 2,
                  py: 1.5
                }}
              >
                Nueva Encuesta
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Surveys Grid */}
        {filteredSurveys.length > 0 ? (
          <Grid container spacing={3}>
            {filteredSurveys.map((survey) => {
              const createdAtDate = formatDate(survey.createdAt);
              const responseRate = survey.preguntas?.length > 0 ? 
                Math.round((survey.respuestas || 0) / (survey.preguntas.length * 10) * 100) : 0; // Assuming 10 responses per question is good

              return (
                <Grid item xs={12} md={6} lg={4} key={survey.id}>
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    backgroundColor: COLORS.surface,
                    borderRadius: 3,
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <CardContent sx={{ flex: 1 }}>
                      {/* Header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" component="h2" sx={{ 
                            fontWeight: 'bold', 
                            color: COLORS.text.primary,
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {survey.título}
                          </Typography>
                        </Box>
                        <Switch
                          checked={survey.active}
                          onChange={() => handleToggleSurveyActive(survey)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: COLORS.status.active,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: COLORS.status.active,
                            },
                          }}
                        />
                      </Box>
                      
                      {/* Status Badge */}
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={survey.active ? 'Activa' : 'Inactiva'}
                          size="small"
                          sx={{
                            backgroundColor: survey.active ? 
                              COLORS.status.active + '20' : 
                              COLORS.status.inactive + '20',
                            color: survey.active ? COLORS.status.active : COLORS.status.inactive,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>

                      {/* Description */}
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        mb: 3, 
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {survey.descripción || 'Sin descripción'}
                      </Typography>

                      {/* Stats */}
                      <Box sx={{ mb: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: COLORS.background, borderRadius: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                                {survey.preguntas?.length || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Preguntas
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: COLORS.background, borderRadius: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                                {survey.respuestas || 0}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Respuestas
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: COLORS.background, borderRadius: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                                {responseRate}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Tasa
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Progress Bar */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progreso
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {responseRate}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={responseRate}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: COLORS.background,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: responseRate > 50 ? COLORS.success : COLORS.warning,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                      
                      {/* Footer */}
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <TodayIcon fontSize="small" sx={{ mr: 0.5 }} />
                          Creada: {createdAtDate}
                        </Typography>
                        
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Ver resultados">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewResults(survey)}
                              sx={{ 
                                color: COLORS.accent,
                                '&:hover': { backgroundColor: COLORS.accent + '20' }
                              }}
                            >
                              <BarChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Vista previa">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewPreview(survey)}
                              sx={{ 
                                color: COLORS.secondary,
                                '&:hover': { backgroundColor: COLORS.secondary + '20' }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar encuesta">
                            <IconButton 
                              size="small"
                              onClick={() => handleEditSurvey(survey)}
                              sx={{ 
                                color: COLORS.primary,
                                '&:hover': { backgroundColor: COLORS.primary + '20' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar encuesta">
                            <IconButton 
                              size="small"
                              onClick={() => handleDeleteSurvey(survey)}
                              sx={{ 
                                color: COLORS.error,
                                '&:hover': { backgroundColor: COLORS.error + '20' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center',
            backgroundColor: COLORS.surface,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <PollIcon sx={{ fontSize: 80, color: COLORS.text.secondary, mb: 3 }} />
            <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 'bold', mb: 2 }}>
              {searchQuery || filterStatus !== 'all' ? 'No se encontraron encuestas' : 'No hay encuestas'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              {searchQuery || filterStatus !== 'all' ? 
                'No se encontraron encuestas que coincidan con los filtros aplicados' : 
                'Aún no has creado ninguna encuesta. Comienza creando tu primera encuesta para recopilar información de los padres.'
              }
            </Typography>
            {searchQuery || filterStatus !== 'all' ? (
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button 
                  variant="outlined" 
                  onClick={() => setSearchQuery('')}
                  sx={{
                    borderColor: COLORS.accent,
                    color: COLORS.accent,
                    '&:hover': { borderColor: COLORS.secondary, backgroundColor: COLORS.accent + '10' }
                  }}
                >
                  Limpiar búsqueda
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => setFilterStatus('all')}
                  sx={{
                    borderColor: COLORS.accent,
                    color: COLORS.accent,
                    '&:hover': { borderColor: COLORS.secondary, backgroundColor: COLORS.accent + '10' }
                  }}
                >
                  Mostrar todas
                </Button>
              </Stack>
            ) : (
              <Button 
                variant="contained" 
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogVisible(true)}
                sx={{
                  backgroundColor: COLORS.accent,
                  '&:hover': { backgroundColor: COLORS.secondary },
                  px: 4,
                  py: 1.5
                }}
              >
                Crear Primera Encuesta
              </Button>
            )}
          </Paper>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: COLORS.accent,
            '&:hover': { backgroundColor: COLORS.secondary }
          }}
          onClick={() => setCreateDialogVisible(true)}
        >
          <AddIcon />
        </Fab>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogVisible} 
          onClose={() => setDeleteDialogVisible(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: COLORS.error + '20' }}>
                <DeleteIcon sx={{ color: COLORS.error }} />
              </Avatar>
              Eliminar Encuesta
            </Box>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: COLORS.text.secondary }}>
              ¿Estás seguro de que deseas eliminar la encuesta <strong>"{surveyToDelete?.título}"</strong>?
              <br /><br />
              Esta acción eliminará permanentemente:
            </DialogContentText>
            <List dense sx={{ mt: 1 }}>
              <ListItem>
                <ListItemIcon>
                  <QuestionAnswerIcon sx={{ color: COLORS.warning }} />
                </ListItemIcon>
                <ListItemText primary="Todas las preguntas configuradas" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PeopleIcon sx={{ color: COLORS.warning }} />
                </ListItemIcon>
                <ListItemText primary={`${surveyToDelete?.respuestas || 0} respuestas de usuarios`} />
              </ListItem>
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Esta acción no se puede deshacer.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setDeleteDialogVisible(false)}
              disabled={deleting}
              sx={{ color: COLORS.text.secondary }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmDeleteSurvey} 
              variant="contained"
              disabled={deleting}
              sx={{
                backgroundColor: COLORS.error,
                '&:hover': { backgroundColor: COLORS.error + 'DD' },
                '&:disabled': { backgroundColor: COLORS.text.secondary }
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                'Eliminar Definitivamente'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Survey Dialog */}
        <Dialog 
          open={createDialogVisible} 
          onClose={() => setCreateDialogVisible(false)} 
          fullWidth 
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }
          }}
        >
          <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: COLORS.accent + '20' }}>
                <AddIcon sx={{ color: COLORS.accent }} />
              </Avatar>
              Nueva Encuesta
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Título de la encuesta"
              fullWidth
              variant="outlined"
              value={newSurveyTitle}
              onChange={(e) => setNewSurveyTitle(e.target.value)}
              placeholder="Ej: Encuesta de satisfacción AMPA 2025"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.accent,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.accent,
                  },
                },
              }}
              error={!newSurveyTitle.trim() && newSurveyTitle.length > 0}
              helperText={!newSurveyTitle.trim() && newSurveyTitle.length > 0 ? 'El título es obligatorio' : ''}
            />
            <TextField
              margin="dense"
              label="Descripción (opcional)"
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              value={newSurveyDescription}
              onChange={(e) => setNewSurveyDescription(e.target.value)}
              placeholder="Describe brevemente el propósito de esta encuesta..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.accent,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: COLORS.accent,
                  },
                },
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              La encuesta se creará como borrador. Podrás agregar preguntas y activarla después.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => {
                setCreateDialogVisible(false);
                setNewSurveyTitle('');
                setNewSurveyDescription('');
              }}
              disabled={creating}
              sx={{ color: COLORS.text.secondary }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateSurvey} 
              variant="contained" 
              disabled={!newSurveyTitle.trim() || creating}
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.secondary },
                '&:disabled': { backgroundColor: COLORS.text.secondary }
              }}
            >
              {creating ? (
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
              ) : (
                'Crear y Configurar'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ 
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default SurveysAdminScreen;